import { useEffect, useState } from "react";
import { CalendarCheck, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { plannerAPI } from "@/lib/planner";
import { toast } from "sonner";

/**
 * CopyWeekDialog — pick a previous week and copy its meals onto the current
 * (or provided) target week. Only writes to empty days on the target so a
 * partial plan there stays put; server returns {copied, skipped_taken,
 * skipped_bad_recipe} so we can tell the user what actually happened.
 *
 * Props:
 *   open              — bool
 *   onClose           — () => void
 *   targetWeek        — target week's Sunday YMD (string)
 *   onCopied          — () => void; fired after a successful copy so the
 *                       caller can refetch the meal plan / shopping list
 */
export default function CopyWeekDialog({ open, onClose, targetWeek, onCopied }) {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingWeek, setSubmittingWeek] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    plannerAPI
      .mealHistory()
      .then((d) => setWeeks((d.weeks || []).filter((w) => w.week_start !== targetWeek)))
      .catch(() => toast.error("Couldn't load your past weeks."))
      .finally(() => setLoading(false));
  }, [open, targetWeek]);

  const copy = async (source_week) => {
    if (!targetWeek) return;
    setSubmittingWeek(source_week);
    try {
      const r = await plannerAPI.copyMealWeek(source_week, targetWeek);
      const parts = [];
      if (r.copied) parts.push(`${r.copied} meal${r.copied === 1 ? "" : "s"} copied`);
      if (r.skipped_taken) parts.push(`${r.skipped_taken} kept (already planned)`);
      if (r.skipped_bad_recipe) parts.push(`${r.skipped_bad_recipe} skipped (recipe removed)`);
      toast.success(parts.join(" · ") || "Week copied.");
      onCopied?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't copy that week.");
    } finally {
      setSubmittingWeek(null);
    }
  };

  const label = (weekStart) => {
    const s = new Date(weekStart + "T00:00:00");
    const e = new Date(weekStart + "T00:00:00");
    e.setDate(e.getDate() + 6);
    return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-lg bg-[#F7F3ED] border-[#A87F56]/30 max-h-[85vh] overflow-y-auto"
        data-testid="planner-copy-week-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl text-[#2A2823]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Reuse a past week
          </DialogTitle>
          <p className="text-xs text-[#2A2823]/60 mt-1">
            Pick a week you loved — we'll copy its recipes onto {targetWeek ? label(targetWeek) : "this week"}.
            Days you've already planned stay put.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-[#2A2823]/50">Loading past weeks…</div>
        ) : weeks.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#2A2823]/60">
            <p>No past weeks yet — once you plan a full week and roll into the next, it'll show up here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#A87F56]/15">
            {weeks.map((w) => (
              <li key={w.week_start}>
                <button
                  onClick={() => copy(w.week_start)}
                  disabled={submittingWeek !== null}
                  data-testid={`planner-copy-week-choice-${w.week_start}`}
                  className="w-full flex items-center gap-3 py-3 px-2 hover:bg-[#A87F56]/5 text-left disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#EDE5DA] flex items-center justify-center shrink-0">
                    <CalendarCheck className="w-4 h-4 text-[#3E4A37]" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[#2A2823]">{label(w.week_start)}</div>
                    <div className="text-xs text-[#2A2823]/55 truncate">
                      {w.count} meal{w.count === 1 ? "" : "s"}
                      {w.titles?.length ? ` · ${w.titles.join(", ")}` : ""}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#2A2823]/40 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
